import { DateTime } from 'luxon';
import { useState } from 'react';
import { DateRangeInput } from '@/features/events/components/InlineFields/DateTime/components/DateRangeInput';
import {
  toEventListBound,
  toEventListDay,
} from '@/features/events/lib/eventListWindow.lib';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useUpdateExperience } from '@/features/experiences/hooks/useUpdateExperience';
import { EventListDisplay } from '@/types/gql.generated';
import { Box } from '@/ui';
import { RadioRow, SidebarCard, SidebarSection } from '../SidebarKit';
import { SectionHeader } from './SectionHeader';

export const ScheduleDatesSection = () => {
  const { experience } = usePageExperienceContext();
  const { updateExperience, setEventListDateRange } = useUpdateExperience();
  const [showPicker, setShowPicker] = useState(false);

  // Mode is derived from saved dates rather than eventListDisplay because some
  // layouts (e.g. planner) don't support eventListDisplay=CUSTOM.
  const isCustom = experience.eventListStartDate !== null;

  const today = toEventListDay(DateTime.now());
  const startDate = experience.eventListStartDate
    ? DateTime.fromISO(experience.eventListStartDate, { zone: 'utc' })
    : today;
  const endDate = experience.eventListEndDate
    ? DateTime.fromISO(experience.eventListEndDate, { zone: 'utc' })
    : today;

  const handleStartChange = (newStart: DateTime) => {
    const nextStart = toEventListDay(newStart);
    // Clamp end to start if the new start is after the current end, so the
    // saved range can never be inverted.
    const nextEnd = nextStart > endDate ? nextStart : endDate;
    setEventListDateRange(
      toEventListBound(nextStart),
      toEventListBound(nextEnd)
    );
  };

  const handleEndChange = (newEnd: DateTime) => {
    const nextEnd = toEventListDay(newEnd);
    // Clamp start to end if the new end is before the current start
    const nextStart = nextEnd < startDate ? nextEnd : startDate;
    setEventListDateRange(
      toEventListBound(nextStart),
      toEventListBound(nextEnd)
    );
  };

  const handlePerpetual = () => {
    setShowPicker(false);
    updateExperience(
      {
        eventListDisplay: EventListDisplay.All,
        eventListStartDate: null,
        eventListEndDate: null,
      },
      { invalidateCollection: true }
    );
  };

  return (
    <Box maxW="560px">
      <SectionHeader
        description="How far the calendar reaches, backward and forward."
        title="Schedule dates"
      />

      <SidebarSection label="Date range">
        <SidebarCard>
          <RadioRow
            isActive={!isCustom && !showPicker}
            label="Perpetual"
            sublabel="Always reaches backward and forward, with or without events."
            variant="dot"
            onClick={handlePerpetual}
          />
          <RadioRow
            isActive={isCustom || showPicker}
            label="Set dates"
            sublabel="Pick a fixed start and end."
            variant="dot"
            onClick={() => setShowPicker(true)}
          />
        </SidebarCard>

        {(isCustom || showPicker) && (
          <Box mt={3}>
            <DateRangeInput
              endDate={endDate}
              hasRecurrence={false}
              isAllDay={false}
              isFluid
              startDate={startDate}
              untilDate={null}
              onEndChange={handleEndChange}
              onStartChange={handleStartChange}
              onUntilChange={() => {}}
            />
          </Box>
        )}
      </SidebarSection>
    </Box>
  );
};
